import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { authAPI } from '../../services/api'
import Button from '../../components/ui/Button'
import Card from '../../components/ui/Card'

const ForgotPasswordPage = () => {
  const { t } = useTranslation('auth')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm()

  const onSubmit = async (data) => {
    setIsLoading(true)
    try {
      await authAPI.forgotPassword(data.email)
      setIsSubmitted(true)
    } catch (err) {
      const message = err.response?.data?.message || t('forgotPassword.errorFallback')
      setError('root', { message })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ background: 'var(--color-bg)' }}>
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1f9f93] p-2" style={{ boxShadow: '0 10px 22px rgba(31,159,147,.22)' }}>
              <img src="/logo.png" alt="Finomi" className="w-10 h-10 object-contain brightness-0 invert" />
            </div>
          </div>
          <h2 className="mt-6 text-[28px] font-black tracking-[.2px]" style={{ color: 'var(--color-text)' }}>
            {t('forgotPassword.title')}
          </h2>
          <p className="mt-2 text-[13px] font-semibold" style={{ color: 'var(--color-text-muted)' }}>
            {t('forgotPassword.subtitle')}
          </p>
        </div>

        <Card>
          {isSubmitted ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(31,159,147,.12)' }}>
                <svg className="w-6 h-6" style={{ color: '#1f9f93' }} fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--color-text)' }}>
                {t('forgotPassword.successTitle')}
              </h3>
              <p className="text-sm font-semibold mb-6" style={{ color: 'var(--color-text-muted)' }}>
                {t('forgotPassword.successMessage')}
              </p>
              <Link
                to="/login"
                className="text-sm font-bold no-underline"
                style={{ color: '#1f9f93' }}
              >
                {t('forgotPassword.backToLogin')}
              </Link>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label htmlFor="email" className="form-label">
                  {t('forgotPassword.emailLabel')}
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="form-input mt-1"
                  placeholder={t('forgotPassword.emailPlaceholder')}
                  {...register('email', {
                    required: t('validation:emailRequired'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('validation:emailInvalid')
                    }
                  })}
                />
                {errors.email && (
                  <p className="form-error mt-1">{errors.email.message}</p>
                )}
              </div>

              {errors.root && (
                <div className="rounded-xl p-3" style={{ background: 'rgba(229,83,83,.10)', border: '1px solid rgba(229,83,83,.18)' }}>
                  <p className="text-sm font-bold" style={{ color: '#e55353' }}>{errors.root.message}</p>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={isLoading}
                className="w-full"
              >
                {t('forgotPassword.submit')}
              </Button>
            </form>
          )}
        </Card>

        {!isSubmitted && (
          <div className="text-center">
            <p className="text-sm font-semibold" style={{ color: 'var(--color-text-muted)' }}>
              {t('forgotPassword.rememberPassword')}{' '}
              <Link
                to="/login"
                className="font-bold no-underline"
                style={{ color: '#1f9f93' }}
              >
                {t('forgotPassword.loginLink')}
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ForgotPasswordPage
